import { ArrowBack } from '@mui/icons-material';
import { Button, createStyles, IconButton, TextField, Typography, useTheme } from '@mui/material';
import { Box } from '@mui/system';
import { FlexRow } from 'common/FlexRow';
import { useConfirmDialog } from 'common/hooks/useConfirmDialog';
import { fabric } from 'fabric';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useEffect, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { OrientationKey } from 'stores/Orientation';
import { ConfirmationDialog } from './ConfirmDialog';
import { useRoot } from './RootProvider';
import { useStructures } from './useStructures';
import { StructurePanel } from './assets/StructurePanel';
import { AssetType } from 'stores/RootStore';
import { AssetPanel } from './assets/AssetPanel';
import { useAssets } from './useAssets';
import { useFabric } from './useFabric';

type StructureEditorPageProps = { type: AssetType };

export const StructureEditorPage = observer((props: StructureEditorPageProps) => {
  const root = useRoot();

  const params = useParams();
  const history = useHistory();
  const store = useStructures();
  const { type } = props;
  
  const [loading, setLoading] = React.useState(false);
  const [loadAttempted, setLoadAttempted] = React.useState(false);

  var displayText;
  switch (type) {
    case AssetType.Floor:
      displayText = 'floor';
      break;
    case AssetType.Wall:
      displayText = 'wall';
      break;
  }
  const { structureId } = params;
  if (!structureId) {
    history.push('/');
    return null;
  }
  
  const structure = store.getStructure(props.type, structureId);

  // Load structure if needed
  React.useEffect(() => {
    const loadStructureIfNeeded = async () => {
      console.log('🏗️ Structure loading check:', {
        hasStructure: !!structure,
        loading,
        loadAttempted,
        storeLoading: store.loadingStructures,
        itemsCount: store.items(type).size,
        structureId
      });
      
      if (!structure && !loading && !loadAttempted) {
        setLoading(true);
        setLoadAttempted(true);
        
        try {
          // Use the new single structure loading method
          const loadedStructure = await store.handleLoadStructureById(structureId, props.type);
          if (!loadedStructure) {
            console.error('Structure not found:', structureId);
            history.replace('/');
            return;
          }
          console.log('🏗️ Structure loaded successfully:', loadedStructure.id);
        } catch (error) {
          console.error('Failed to load structure:', error);
          history.replace('/');
          return;
        } finally {
          setLoading(false);
        }
      }
    };

    loadStructureIfNeeded();
  }, [structureId, structure, store, history, loading, loadAttempted, type, props.type]);

  // Show loading state while structure is being loaded
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Typography>Loading structure...</Typography>
      </Box>
    );
  }

  // Only redirect if we've attempted to load and structure is still not found
  if (!structure && !loading && loadAttempted) {
    history.replace('/');
    return null;
  }

  // If we haven't loaded the structure yet, show loading (this prevents immediate redirect)
  if (!structure && !loadAttempted) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Typography>Initializing...</Typography>
      </Box>
    );
  }
  
  //add floor to canvas?

  return (
    <Box width='100vw' height='120vh' display='flex'>
      <Box height='100%' width='80%' display='flex' flexDirection='column'>
        <FlexRow>
          <IconButton
            onClick={() => {
              history.push(`/${displayText}`);
            }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant='h3'>{structure.id}</Typography>
        </FlexRow>
        <StructurePanel id={structureId} structureDO={structure} />
      </Box>
    </Box>
  );
});
